import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useRouteMatch, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Grid
} from '@material-ui/core';

import { fetchTread, selectFetchStatus, selectAccess, resetStatus } from './treadSlice';

import TopSection from '../common/topSection';
import BreadCrumb from '../common/breadcrumb';
import Alert from '../common/alert';
import Loading from '../common/loading';
import PinButton from '../pins/PinButton';

import StatusTabs from '../itemFamily/StatusTabs';
import TreadPagePC24 from './treadPagePC24';

import useTitle from '../../hooks/use-title.hook';

const ownerSpecificComponents = {
  PC24: TreadPagePC24
};

const Tread = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { itemOwnerId, treadId } = useParams();
  const routeMatch = useRouteMatch();
  const { fetchStatus, fetchError, ...tread } = useSelector(selectFetchStatus);
  const canEdit = useSelector(selectAccess);

  const breadcrumbItems = [
    {name: itemOwnerId, link: `/pih/owner/${itemOwnerId}`},
    {name: t('nav:treads'), link: `/pih/owner/${itemOwnerId}/treads`},
    tread.draft.name ? {name: tread.draft.name} : {name: treadId}
  ].filter(Boolean);

  const statusTabs = [
    {id: 0, url: routeMatch.url, label: t('tread:draft')},
    {id: 1, url: `${routeMatch.url}/published`, label: t('tread:published'), disabled: tread.published === null}
  ];

  useEffect(() => {
    // Fetch data from server
    dispatch(fetchTread({ownerId: itemOwnerId, treadId: treadId}));

    return () => {
      // Empty state on unmount
      // ckeditor causes error if it is unmounted before it has been initialised (the fraction the old family is visible before useEffect fetches new data)
      dispatch(resetStatus());
    }
  }, [dispatch, treadId]);

  useTitle(tread?.draft?.name);

  return (
    <>
      <TopSection>
        <Grid container justify="space-between" alignItems="center">
          <Grid item>
            <BreadCrumb items={breadcrumbItems} />
          </Grid>
          <Grid item>
            {tread.draft.code ? <PinButton itemCode={tread.draft.code} itemName={tread.draft.name} itemOwner={itemOwnerId} itemType="tread" /> : null }
          </Grid>
        </Grid>
      </TopSection>

      <Box p={3.5}>
        <StatusTabs tabs={statusTabs} />
      </Box>

      <Box p={3.5} pb="70px">
        {fetchStatus === 'loading' ? <Loading /> : null}
        {fetchStatus === 'failed' ? <Alert severity="error">{t(`errors:${fetchError}`)}</Alert> : null}
        {fetchStatus === 'succeeded'
          ?
          <Route path={`${routeMatch.path}/:status?`} render={routeProps => {
            const status = routeProps.match.params.status || 'draft'; // default to draft (default is the one without url param)
            const data = tread[status];
            const Component = ownerSpecificComponents[itemOwnerId];

            if(Component) {
              // Key forces it to re-mount when status changes
              return <Component key={status} status={status} tread={data} canEdit={canEdit} />;
            } else {
              return <Alert severity="error">View not implemented for owner {itemOwnerId}</Alert>;
            }
          }}
          />
          :
          null
        }
      </Box>
    </>
  );
};

export default Tread;
